'use client';

import type { CardData, EncryptionRequest, EncryptionResponse, PaymentMethodDataEncCardForWorldpay } from '@payments/aurum-contracts';
import { API_ROUTES, useApiCall } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { useClientConfig } from '@shared/utils';

type SeonCardSource = {
  cardNumber?: string;
  expiryMonth?: string;
  expiryYear?: string;
  cardholderName?: string;
};

const SEON_ENCRYPTION_TIMEOUT_MS = 3000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('SEON card encryption timed out')), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

function buildSeonCardData(data: SeonCardSource): CardData {
  const cardNumber = (data.cardNumber ?? '').replace(/\D/g, '');

  return {
    bin: cardNumber.slice(0, 6),
    lastDigits: cardNumber.slice(-4),
    expiryMonth: data.expiryMonth ?? '',
    expiryYear: data.expiryYear ?? '',
    fullName: (data.cardholderName ?? '').trim(),
  };
}

function useSeonCardEncryption() {
  const apiCall = useApiCall();
  const { apiUrl } = useClientConfig();
  const debug = createDebug({ isEnabled: false });

  const encryptSeonCardData = async (data: SeonCardSource): Promise<PaymentMethodDataEncCardForWorldpay['encCardData']> => {
    const body: EncryptionRequest = { toEncrypt: JSON.stringify(buildSeonCardData(data)) };
    const url = `${apiUrl}${API_ROUTES.PAYMENTS.NUCLEUS_ENCRYPT}`;

    try {
      const response = await withTimeout(
        apiCall<EncryptionResponse>(url, 'POST', {
          credentials: false,
          headers: { apiKey: '' },
          body,
        }),
        SEON_ENCRYPTION_TIMEOUT_MS
      );

      if (!response?.encrypted) {
        debug.forceError('SEON card encryption returned an empty response');
        return undefined;
      }

      return response.encrypted;
    } catch (error) {
      debug.forceError('SEON card encryption failed', error);
      return undefined;
    }
  };

  return { encryptSeonCardData };
}

export { buildSeonCardData, useSeonCardEncryption };
export type { SeonCardSource };
